import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { GB_PATH, IE_PATH, CITIES } from './ukMapData';
import { Reveal, RevealText } from '../motion/Reveal';
import { useMotionMode } from '../../contexts/MotionContext';

const BY_NAME = Object.fromEntries(CITIES.map((c) => [c.name, c]));
const HOME = BY_NAME.Manchester.p;
const REGIONS = ['England', 'Scotland', 'Wales', 'Northern Ireland'];
const ROUTE_MS = 3400;
const STATIC_ROUTES = ['London', 'Edinburgh', 'Glasgow', 'Cardiff', 'Belfast', 'Newcastle', 'Bristol', 'Norwich'];

const curve = ([ax, ay], [bx, by], k) => {
  const cx = (ax + bx) / 2 - (by - ay) * k;
  const cy = (ay + by) / 2 + (bx - ax) * k;
  return `M${ax},${ay} Q${cx},${cy} ${bx},${by}`;
};

const pick = (exclude) => {
  let c;
  do { c = CITIES[Math.floor(Math.random() * CITIES.length)]; } while (c.name === exclude);
  return c;
};

const LiveRoute = ({ from, to, k }) => {
  const [x, y] = to.p;
  return (
    <g>
      <motion.path d={curve(from.p, to.p, k)} fill="none" stroke="white" strokeWidth="1.3" strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: [0, 1, 1], opacity: [0, 0.9, 0] }}
        transition={{ duration: ROUTE_MS / 1000, times: [0, 0.5, 1], ease: 'easeInOut' }} />
      <motion.circle cx={x} cy={y} fill="none" stroke="white" initial={{ r: 3, opacity: 0 }}
        animate={{ r: [3, 3, 18], opacity: [0, 0.9, 0] }} transition={{ duration: 1.6, delay: 1.5, times: [0, 0.1, 1] }} />
    </g>
  );
};

const useLiveRoutes = (active) => {
  const [routes, setRoutes] = useState([]);
  const id = useRef(0);
  useEffect(() => {
    if (!active) return undefined;
    const timers = [];
    const spawn = () => {
      const from = Math.random() < 0.45 ? BY_NAME.Manchester : pick();
      const to = pick(from.name);
      const key = ++id.current;
      setRoutes((r) => [...r.slice(-14), { key, from, to, k: (Math.random() - 0.5) * 0.6 }]);
      timers.push(setTimeout(() => setRoutes((r) => r.filter((x) => x.key !== key)), ROUTE_MS + 200));
    };
    const iv = setInterval(spawn, 420);
    return () => { clearInterval(iv); timers.forEach(clearTimeout); setRoutes([]); };
  }, [active]);
  return routes;
};

const CityDot = ({ c, i, show }) => {
  const [x, y] = c.p;
  const dx = c.anchor === 'end' ? -9 : 9;
  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: show ? 1 : 0 }} transition={{ duration: 0.6, delay: show ? i * 0.04 : 0 }} data-testid={`map-city-${c.name.toLowerCase().replace(/\s+/g, '-')}`}>
      <circle cx={x} cy={y} r="3.2" fill="white" />
      {c.label && (
        <text x={x + dx} y={y + (c.dy ?? 4)} textAnchor={c.anchor} className="fill-white/80 text-[17px] md:text-[11px] tracking-wide">{c.name}</text>
      )}
    </motion.g>
  );
};

const MapSvg = ({ show, routes, reduced }) => (
  <svg viewBox="110 20 580 760" className="w-full max-h-[85vh]" role="img" aria-label="Map of the UK showing journeys between major cities" data-testid="journey-map-svg">
    <path d={IE_PATH} fill="rgba(255,255,255,0.025)" stroke="rgba(255,255,255,0.12)" strokeWidth="0.8" />
    <path d={GB_PATH} fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" />
    {reduced
      ? STATIC_ROUTES.map((n) => <path key={n} d={curve(HOME, BY_NAME[n].p, 0.2)} fill="none" stroke="white" strokeOpacity="0.6" strokeWidth="1.2" />)
      : <g data-testid="map-live-routes">{routes.map((r) => <LiveRoute key={r.key} {...r} />)}</g>}
    {CITIES.filter((c) => c.name !== 'Manchester').map((c, i) => <CityDot key={c.name} c={c} i={i} show={show} />)}
    <circle cx={HOME[0]} cy={HOME[1]} r="6.5" fill="white" />
    <motion.circle cx={HOME[0]} cy={HOME[1]} r="7" fill="none" stroke="white" animate={{ r: [7, 26], opacity: [0.8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }} />
    <text x={HOME[0] + 12} y={HOME[1] + 18} className="fill-white text-[20px] md:text-[14px] font-bold tracking-wider uppercase">Manchester</text>
  </svg>
);

export const JourneyMap = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.25 });
  const { reduced } = useMotionMode();
  const routes = useLiveRoutes(inView && !reduced);

  return (
    <section ref={ref} className="relative bg-black text-white py-24 lg:py-32 overflow-hidden" data-testid="journey-map">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.3em] text-white/50 mb-4">Journeys</p>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6"><RevealText text="Manchester-based. UK-wide." /></h2>
          <Reveal className="text-white/60 text-lg leading-relaxed mb-10 max-w-md">
            Airport runs, business trips and long-distance journeys to every corner of the UK, door to door.
          </Reveal>
          <div className="grid grid-cols-2 gap-x-6 gap-y-8">
            {REGIONS.map((r, i) => (
              <Reveal key={r} delay={i * 0.08}>
                <p className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-3">{r}</p>
                <p className="text-sm text-white/75 leading-relaxed" data-testid={`map-region-${i}`}>
                  {CITIES.filter((c) => c.region === r).map((c) => c.name).join(' · ')}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-7">
          <MapSvg show={inView || reduced} routes={routes} reduced={reduced} />
        </div>
      </div>
    </section>
  );
};
